import React, { useEffect, useState } from 'react';
import axios from 'axios';

// User-facing messages for the ?error redirects from the OAuth callback.
const ERROR_MESSAGES = {
  not_in_beta:     'The CAAM staff portal is currently in a limited beta. Contact IT at it@caamuseum.org to request access.',
  account_disabled:'Your account is disabled. Please contact IT at it@caamuseum.org.',
  invalid_domain:  'Please sign in with your @caamuseum.org Google account.',
  oauth_failed:    'Sign-in didn’t complete. Please try again.',
  invalid_client:  'Sign-in configuration error. Please contact IT.',
};

// CAAM landing page — direct port of the HTML+CSS Michael provided.
// The only divergences from the source are JSX syntax and the Sign In click handler
// that initiates the Google OAuth flow. Everything else (layout, fonts, image path,
// vignette, side-nav positioning, mobile breakpoints) is byte-for-byte the same.

export default function LandingPage() {
  const [notice, setNotice] = useState(null);

  // Apply the landing page's global resets (html, body, *) at mount, restore on unmount.
  // This keeps the existing app's body styles from bleeding into the design.
  useEffect(() => {
    document.body.classList.add('caam-landing-active');
    const err = new URLSearchParams(window.location.search).get('error');
    if (err && ERROR_MESSAGES[err]) setNotice(ERROR_MESSAGES[err]);
    return () => document.body.classList.remove('caam-landing-active');
  }, []);

  const handleSignIn = async (e) => {
    if (e) e.preventDefault();
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/google/url?clientCode=caam`
      );
      window.location.href = response.data.url;
    } catch (err) {
      console.error('Google OAuth error:', err);
      alert('Unable to start Google sign-in. Please try again.');
    }
  };

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@500;600;700&display=swap" rel="stylesheet" />

      <style>{`
        body.caam-landing-active,
        body.caam-landing-active html {
          margin: 0;
          padding: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
          font-family: 'Oswald', sans-serif;
          background: #000;
        }
        body.caam-landing-active * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .hero {
          position: relative;
          width: 100vw;
          height: 100vh;
          overflow: hidden;
        }

        /* BACKGROUND IMAGE — no brightness reduction so the mirror detail comes through */
        .hero img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        /* SUBTLE VIGNETTE — soft edge darkening only, lets the photo breathe */
        .overlay {
          position: absolute;
          inset: 0;
          background:
            radial-gradient(
              circle at center,
              rgba(0,0,0,0) 0%,
              rgba(0,0,0,0.10) 75%,
              rgba(0,0,0,0.25) 100%
            );
          z-index: 1;
        }

        /* MASSIVE TOP TITLE */
        .title-top {
          position: absolute;
          top: -48px;
          left: 0;
          width: 100%;
          color: white;
          font-size: 10vw;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          white-space: nowrap;
          z-index: 3;
          line-height: 0.9;
          padding-left: 18px;
        }

        /* MASSIVE BOTTOM TITLE — right-justified per Michael 2026-05-29 */
        .title-bottom {
          position: absolute;
          bottom: -58px;
          left: 0;
          width: 100%;
          color: white;
          font-size: 10vw;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          white-space: nowrap;
          z-index: 3;
          line-height: 0.9;
          padding-right: 18px;
          text-align: right;
        }

        /* SIDE NAV */
        .side-nav {
          position: absolute;
          top: 165px;
          left: 42px;
          z-index: 5;
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        /* HAMBURGER */
        .menu-btn {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          border: 1px solid rgba(255,255,255,0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          background: rgba(0,0,0,0.18);
          backdrop-filter: blur(8px);
          transition: 0.3s ease;
        }
        .menu-btn:hover {
          transform: scale(1.06);
          background: rgba(255,255,255,0.08);
        }
        .hamburger {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .hamburger span {
          width: 24px;
          height: 2px;
          background: white;
          display: block;
        }

        /* LINE */
        .divider {
          width: 105px;
          height: 1px;
          background: rgba(255,255,255,0.45);
        }

        /* SIGN IN */
        .signin {
          color: white;
          text-decoration: none;
          font-size: 2rem;
          font-weight: 300;
          letter-spacing: 1px;
          transition: 0.3s ease;
        }
        .signin:hover { opacity: 0.7; }

        /* MOBILE */
        @media (max-width: 900px) {
          .title-top {
            font-size: 18vw;
            top: -18px;
          }
          .title-bottom {
            font-size: 18vw;
            bottom: -18px;
          }
          .side-nav {
            left: 20px;
            top: 115px;
          }
          .menu-btn {
            width: 58px;
            height: 58px;
          }
          .signin {
            font-size: 1.2rem;
          }
        }
      `}</style>

      <section className="hero">
        {/* IMAGE */}
        <img src="/REFLECTIONS_VIEW_9.jpg" alt="Museum Art" />

        {/* NOTICE (e.g. limited-beta block) */}
        {notice && (
          <div
            onClick={() => setNotice(null)}
            style={{
              position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 10,
              background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(6px)',
              color: '#fff', fontFamily: "'Oswald', sans-serif", fontWeight: 400,
              fontSize: '15px', letterSpacing: '0.3px', textAlign: 'center',
              padding: '14px 48px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.15)'
            }}
            title="Dismiss"
          >
            {notice}
          </div>
        )}

        {/* OVERLAY */}
        <div className="overlay"></div>

        {/* TOP TEXT */}
        <h1 className="title-top">California African</h1>

        {/* SIDE NAV */}
        <div className="side-nav">
          <div className="menu-btn" onClick={handleSignIn}>
            <div className="hamburger">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>

          <div className="divider"></div>

          <a href="#" className="signin" onClick={handleSignIn}>
            SIGN IN
          </a>
        </div>

        {/* BOTTOM TEXT */}
        <h1 className="title-bottom">American Museum</h1>
      </section>
    </>
  );
}
